import { onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

/**
 * Motion · Like — Figma `3336:109993`: the heart answers her tap.
 *
 * Played when HER tap changed the state: `arm()` on click, the motion runs
 * once the liked state flips. A favourite that arrives from the server, rolls
 * back after a failed save, or a guest's tap that opened sign-in — no motion.
 * A fast second tap restarts from the current state instead of queueing.
 * The class goes on the button; the CSS scales its `.fair-icon` only, so the
 * 44 px hit area never moves.
 */
const LIKE_CLASS = 'fair-like-motion--like'
const UNLIKE_CLASS = 'fair-like-motion--unlike'
const SETTLED_MS = 400
/** The state may flip after the save answers, not on the tap itself. */
const ARMED_MS = 1500

export function useFairLikeMotion(liked: MaybeRefOrGetter<boolean>) {
  const motionClass = ref<string | null>(null)
  let frame = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let armedUntil = 0

  function arm() {
    armedUntil = Date.now() + ARMED_MS
  }

  function play(liked: boolean) {
    cancelAnimationFrame(frame)
    clearTimeout(timer)
    motionClass.value = null
    // one frame without the class, so the same animation can start again
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        motionClass.value = liked ? LIKE_CLASS : UNLIKE_CLASS
        timer = setTimeout(() => { motionClass.value = null }, SETTLED_MS)
      })
    })
  }

  watch(() => toValue(liked), (next) => {
    if (Date.now() > armedUntil) return
    armedUntil = 0
    play(next)
  })

  onBeforeUnmount(() => {
    cancelAnimationFrame(frame)
    clearTimeout(timer)
  })

  return { motionClass, arm }
}
